// Step 5 (Instantly) - the sending and warmup profile, tracking, and the tags.
//
// Twin of StepParams with Instantly's own fields. Same seeding rule
// (run.params when the run has been here before, CANONICAL_SQ_INSTANTLY_PARAMS
// when not; the canonical object is read-only), same string-held numeric state,
// same shared TagsPanel. The shell mounts this component only for instantly
// runs.
//
// The one field with real consequences is `include_tracking`, PRESELECTED on:
// when included, the run creates an inst.<domain> CNAME to Instantly's tracking
// host in our Cloudflare for every sending domain and attaches it per account.
// Instantly issues an SSL cert per domain asynchronously (minutes to hours), so
// stragglers surface as QA errors with a "Retry failed later" message rather
// than blocking the connect.

import { useState } from 'react';

import {
  CANONICAL_SQ_INSTANTLY_PARAMS,
  sqApi,
  sqErrorMessage,
  type SqInstantlyParams,
} from '../sequencerApi';
import { TagsPanel } from './TagsPanel';
import type { SqStepProps } from './shared';

type NumKey =
  | 'daily_limit'
  | 'sending_gap'
  | 'warmup_limit'
  | 'warmup_increment'
  | 'reply_rate_pct'
  | 'open_rate'
  | 'spam_save_rate'
  | 'important_rate';

/** enable_warmup keeps the feature's 'yes'/'no' convention; the rest are the
 *  booleans they map to in warmup.advanced (plus include_tracking). */
type BoolKey = 'include_tracking' | 'weekday_only' | 'read_emulation' | 'warm_ctd';

/** Matches nonNegativeInt in the params route. */
const MAX_INT = 10_000;

/** Matches warmup_custom_ftag's bound in the params route. */
const FTAG_MAX = 64;

const NUM_FIELDS: { key: NumKey; label: string; hint: string; max: number; suffix?: string }[] = [
  {
    key: 'daily_limit',
    label: 'Daily limit',
    hint: 'Campaign emails per day, per mailbox.',
    max: MAX_INT,
  },
  {
    key: 'sending_gap',
    label: 'Sending gap',
    hint: 'Minutes Instantly waits between two sends from this mailbox.',
    max: MAX_INT,
  },
  {
    key: 'warmup_limit',
    label: 'Warmup daily limit',
    hint: 'Warmup emails per day, per mailbox.',
    max: MAX_INT,
  },
  {
    key: 'warmup_increment',
    label: 'Warmup increment',
    hint: 'Added to the warmup volume each day.',
    max: MAX_INT,
  },
  {
    key: 'reply_rate_pct',
    label: 'Warmup reply rate',
    hint: 'Share of warmup mail that gets a reply.',
    max: 100,
    suffix: '%',
  },
  {
    key: 'open_rate',
    label: 'Open rate',
    hint: 'Warmup advanced: share of warmup mail opened.',
    max: 100,
    suffix: '%',
  },
  {
    key: 'spam_save_rate',
    label: 'Spam protection',
    hint: 'Warmup advanced: share rescued from spam.',
    max: 100,
    suffix: '%',
  },
  {
    key: 'important_rate',
    label: 'Mark important',
    hint: 'Warmup advanced: share marked important.',
    max: 100,
    suffix: '%',
  },
];

const BOOL_TOGGLES: { key: BoolKey; label: string; hint: string }[] = [
  {
    key: 'include_tracking',
    label: 'Custom tracking domain',
    hint:
      'Creates an inst.<domain> CNAME to prox.itrackly.com in our Cloudflare for every ' +
      'sending domain and attaches it to each account. Instantly issues an SSL cert per ' +
      'domain asynchronously (minutes to hours) - stragglers show as QA errors and clear ' +
      'on a later Retry failed. Switch off to connect without click tracking.',
  },
  {
    key: 'weekday_only',
    label: 'Weekdays only',
    hint: 'Warmup advanced: send warmup mail on weekdays only.',
  },
  {
    key: 'read_emulation',
    label: 'Read emulation',
    hint: 'Warmup advanced: emulate reading time on warmup mail.',
  },
  {
    key: 'warm_ctd',
    label: 'Warm custom tracking domain',
    hint: 'Warms the inst. tracking links too. Off in our canonical profile.',
  },
];

/** Same rule as StepParams: a whole non-negative integer inside the field's own
 *  ceiling, or null. */
function parseNum(raw: string, max: number): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 && n <= max ? n : null;
}

export function StepParamsInstantly({
  runId,
  detail,
  reload,
  goToStep,
  toast,
}: SqStepProps): JSX.Element {
  const { run } = detail;

  // The cast is guarded by the shell: this component mounts only when
  // run.sequencer === 'instantly', so run.params is the Instantly shape.
  const [nums, setNums] = useState<Record<NumKey, string>>(() => {
    const seed = (run.params as SqInstantlyParams | null) ?? CANONICAL_SQ_INSTANTLY_PARAMS;
    return {
      daily_limit: String(seed.daily_limit),
      sending_gap: String(seed.sending_gap),
      warmup_limit: String(seed.warmup_limit),
      warmup_increment: String(seed.warmup_increment),
      reply_rate_pct: String(seed.reply_rate_pct),
      open_rate: String(seed.open_rate),
      spam_save_rate: String(seed.spam_save_rate),
      important_rate: String(seed.important_rate),
    };
  });

  const [enableWarmup, setEnableWarmup] = useState<'yes' | 'no'>(() => {
    const seed = (run.params as SqInstantlyParams | null) ?? CANONICAL_SQ_INSTANTLY_PARAMS;
    return seed.enable_warmup;
  });

  const [bools, setBools] = useState<Record<BoolKey, boolean>>(() => {
    const seed = (run.params as SqInstantlyParams | null) ?? CANONICAL_SQ_INSTANTLY_PARAMS;
    return {
      include_tracking: seed.include_tracking,
      weekday_only: seed.weekday_only,
      read_emulation: seed.read_emulation,
      warm_ctd: seed.warm_ctd,
    };
  });

  // Both arrived after the first Instantly runs shipped, so a run saved before
  // then has neither key - the ?? is what keeps re-editing such a run working.
  const [ftag, setFtag] = useState<string>(() => {
    const seed = (run.params as SqInstantlyParams | null) ?? CANONICAL_SQ_INSTANTLY_PARAMS;
    return seed.warmup_custom_ftag ?? '';
  });

  const [slowRamp, setSlowRamp] = useState<boolean>(() => {
    const seed = (run.params as SqInstantlyParams | null) ?? CANONICAL_SQ_INSTANTLY_PARAMS;
    return seed.enable_slow_ramp ?? false;
  });

  const [existingIds, setExistingIds] = useState<string[]>(
    () => [...(run.params?.extra_tags.existing_ids ?? [])],
  );
  const [newNames, setNewNames] = useState<string[]>(
    () => [...(run.params?.extra_tags.new_names ?? [])],
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    const parsed = {} as Record<NumKey, number>;
    for (const f of NUM_FIELDS) {
      const value = parseNum(nums[f.key], f.max);
      if (value === null) {
        setError(`${f.label} must be a whole number between 0 and ${f.max}.`);
        return;
      }
      parsed[f.key] = value;
    }

    const trimmedFtag = ftag.trim();
    if (trimmedFtag.length > FTAG_MAX) {
      setError(`Warmup custom ftag must be at most ${FTAG_MAX} characters.`);
      return;
    }

    const params: SqInstantlyParams = {
      ...parsed,
      enable_warmup: enableWarmup,
      ...bools,
      enable_slow_ramp: slowRamp,
      warmup_custom_ftag: trimmedFtag,
      // Same rule as the PlusVibe step: `resolved_ids` is Start's alone.
      extra_tags: { existing_ids: [...existingIds], new_names: [...newNames] },
    };

    setBusy(true);
    setError(null);
    try {
      await sqApi.setParams(runId, params);
      await reload();
      await goToStep(6);
    } catch (err) {
      setError(sqErrorMessage(err));
      toast('error', sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 5 - Parameters</h2>
        <p>
          The sending, warmup and tracking profile pushed to every mailbox in this run, prefilled
          with our standard values. Change one only when the customer's order says so.
        </p>
      </div>
      <div className="sq-step-body">
        {error && <div className="sq-notice sq-notice--err">{error}</div>}

        <div className="sq-numgrid">
          {NUM_FIELDS.map((f) => (
            <div className="sq-field" key={f.key}>
              <label className="sq-field-label" htmlFor={`sq-param-${f.key}`}>
                {f.label}
                {f.suffix ? ` (${f.suffix})` : ''}
              </label>
              <input
                id={`sq-param-${f.key}`}
                className="sq-input sq-mono"
                type="number"
                inputMode="numeric"
                min={0}
                max={f.max}
                step={1}
                value={nums[f.key]}
                onChange={(e) => setNums({ ...nums, [f.key]: e.target.value })}
                disabled={busy}
              />
              <span className="sq-hint">{f.hint}</span>
            </div>
          ))}
        </div>

        <div className="sq-togglegrid">
          <div className="sq-field">
            <span className="sq-field-label">Warmup</span>
            <div className="sq-toggle">
              {(['yes', 'no'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`sq-toggle-btn${enableWarmup === v ? ' sq-toggle-btn--on' : ''}`}
                  onClick={() => setEnableWarmup(v)}
                  disabled={busy}
                  aria-pressed={enableWarmup === v}
                >
                  {v === 'yes' ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
            <span className="sq-hint">Run Instantly warmup on every connected mailbox.</span>
          </div>

          {BOOL_TOGGLES.map((t) => (
            <div className="sq-field" key={t.key}>
              <span className="sq-field-label">{t.label}</span>
              <div className="sq-toggle">
                {([true, false] as const).map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    className={`sq-toggle-btn${bools[t.key] === v ? ' sq-toggle-btn--on' : ''}`}
                    onClick={() => setBools({ ...bools, [t.key]: v })}
                    disabled={busy}
                    aria-pressed={bools[t.key] === v}
                  >
                    {v ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
              <span className="sq-hint">{t.hint}</span>
            </div>
          ))}
        </div>

        <div className="sq-field">
          <label className="sq-field-label" htmlFor="sq-param-warmup_custom_ftag">
            Warmup custom ftag (optional)
          </label>
          <input
            id="sq-param-warmup_custom_ftag"
            className="sq-input sq-mono"
            type="text"
            maxLength={FTAG_MAX}
            value={ftag}
            onChange={(e) => setFtag(e.target.value)}
            disabled={busy}
          />
          <span className="sq-hint">
            Instantly's warmup pool tag. It lives inside the account's warmup object, which every
            config push REPLACES wholesale - so a value set by hand in Instantly is wiped unless it
            is entered here. Leave empty when the customer's order does not name one.
          </span>
        </div>

        <div className="sq-field">
          <span className="sq-field-label">Slow ramp</span>
          <label className="sq-check">
            <input
              type="checkbox"
              checked={slowRamp}
              onChange={(e) => setSlowRamp(e.target.checked)}
              disabled={busy}
            />
            <span>Enable slow ramp</span>
          </label>
          <span className="sq-hint">
            Instantly raises the campaign sending volume gradually instead of starting at the daily
            limit. A top-level account setting, not a warmup one. Off in our canonical profile.
          </span>
        </div>

        <TagsPanel
          runId={runId}
          tagName={run.tag_name}
          existingIds={existingIds}
          setExistingIds={setExistingIds}
          newNames={newNames}
          setNewNames={setNewNames}
          busy={busy}
        />

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? 'Saving...' : 'Save & continue'}
          </button>
        </div>
      </div>
    </section>
  );
}
